import React, { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import DCPromoBanner from "@/components/DCPromoBanner";
import Hero from "@/components/Hero";
import LogoMarquee from "@/components/LogoMarquee";
import Portfolio from "@/components/Portfolio";
import ImageBreak from "@/components/ImageBreak";
import { Button } from "@/components/ui/button";
import { LazySection } from "@/components/LazySection";
import { PROPERTY_IMAGES } from "@/lib/propertyImages";
import { BLOG_POSTS } from "@/lib/blogPosts";
import { Calendar, Clock, ArrowRight } from "lucide-react";

// Lazy load below-the-fold components
const Testimonials = lazy(() => import("@/components/Testimonials"));
const CTASection = lazy(() => import("@/components/CTASection"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Comparison = lazy(() => import("@/components/Comparison"));
const PricingEducation = lazy(() => import("@/components/PricingEducation"));
const Pricing = lazy(() => import("@/components/Pricing"));
const CoHostComparison = lazy(() => import("@/components/CoHostComparison"));
const FounderBio = lazy(() => import("@/components/FounderBio"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Footer = lazy(() => import("@/components/Footer"));
const CountyCompliance = lazy(() => import("@/components/CountyCompliance"));
const FullServiceManagement = lazy(
  () => import("@/components/FullServiceManagement"),
);
const WatchLearnSection = lazy(() => import("@/components/WatchLearnSection"));

const recentPosts = BLOG_POSTS.slice(0, 3);

const Index = () => {
  const revealRef = useReveal<HTMLDivElement>();

  const scrollToForm = () => {
    const formElement = document.getElementById("lead-form");
    if (formElement) {
      window.dispatchEvent(new Event("force-load-form"));
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      ref={revealRef}
      className="min-h-screen bg-background font-body selection:bg-primary/30 selection:text-primary"
    >
      <SEOHead
        title="Airbnb Management in DC, Alexandria & Arlington VA | JNN Homes"
        description="Full-service Airbnb management for homeowners in Alexandria City, Arlington, Fairfax, Loudoun, Prince William and Washington, D.C. neighborhoods like Capitol Hill, Navy Yard and Georgetown."
        canonical="https://www.jnnhomes.com"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "JNN Homes",
            alternateName: "JNN Management LLC",
            image:
              "https://vibe.filesafe.space/meta/1778679421893490393/og_image.png",
            "@id": "https://www.jnnhomes.com",
            url: "https://www.jnnhomes.com",
            telephone: "+17038905425",
            priceRange: "15%–25% management fee",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Northern Virginia",
              addressRegion: "VA",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 38.8816,
              longitude: -77.091,
            },
            areaServed: [
              { "@type": "City", name: "Alexandria City, VA" },
              { "@type": "City", name: "Arlington, VA" },
              { "@type": "AdministrativeArea", name: "Fairfax County, VA" },
              { "@type": "AdministrativeArea", name: "Loudoun County, VA" },
              {
                "@type": "AdministrativeArea",
                name: "Prince William County, VA",
              },
              { "@type": "City", name: "Washington, DC" },
            ],
            description:
              "Full-service Airbnb management for homeowners in Northern Virginia and Washington, D.C.",
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "00:00",
              closes: "23:59",
            },
            sameAs: ["https://www.airbnb.com", "https://www.jnnhomes.com"],
          },
          {
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            name: "JNN Homes",
            image:
              "https://vibe.filesafe.space/meta/1778679421893490393/og_image.png",
            "@id": "https://www.jnnhomes.com/#realestate",
            url: "https://www.jnnhomes.com",
            telephone: "+17038905425",
            address: {
              "@type": "PostalAddress",
              addressRegion: "VA",
              addressCountry: "US",
            },
            areaServed: [
              "Alexandria City, VA",
              "Arlington, VA",
              "Fairfax County, VA",
              "Loudoun County, VA",
              "Prince William County, VA",
              "Washington, DC",
            ],
            description:
              "Full-service Airbnb management for homeowners across VA & DC.",
          },
        ]}
      />
      <Navbar />
      <DCPromoBanner />

      <main>
        {/* 1. Hero — split-screen with premium property photography */}
        <Hero />

        {/* Service-area band */}
        <section className="py-5 bg-primary/5 border-b border-border/60">
          <div className="container mx-auto px-4">
            <p className="reveal text-center text-xs md:text-sm text-muted-foreground leading-relaxed max-w-4xl mx-auto">
              Serving Alexandria City and Arlington first, plus Fairfax,
              Loudoun, Prince William and Washington, D.C., including Capitol
              Hill, Navy Yard, Dupont Circle, Logan Circle, Shaw, U Street,
              Adams Morgan, Columbia Heights, Petworth, Bloomingdale, H
              Street/NoMa, Georgetown, Foggy Bottom and the Southwest
              Waterfront.
            </p>
          </div>
        </section>

        {/* 2. Platform Logos / Trust Bar */}
        <LogoMarquee />

        {/* 3. Portfolio / Properties We Manage */}
        <LazySection
          minHeight="700px"
          fallback={
            <div className="h-[700px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense fallback={null}>
            <Portfolio />
          </Suspense>
        </LazySection>

        {/* Cinematic full-width image break */}
        <ImageBreak
          image={PROPERTY_IMAGES.livingRoom}
          alt="Professionally staged luxury living room managed by JNN Homes"
          eyebrow="Professional Management"
          title="Exceptional Guest Experiences, Every Stay."
        />

        {/* 4. Meet Jason (Founder Bio) */}
        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FounderBio />
          </Suspense>
        </LazySection>

        {/* 5. Testimonials (First Wave) — alternating photo/review */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Testimonials variant={1} />
          </Suspense>
        </LazySection>

        {/* 6. Simple 3-Step Process — horizontal timeline */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <HowItWorks />
          </Suspense>
        </LazySection>

        {/* Mid-page CTA */}
        <div className="py-12 bg-background flex justify-center px-4">
          <Button
            onClick={scrollToForm}
            className="reveal bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.03] active:scale-95 text-base md:text-lg font-bold py-4 md:py-6 px-6 md:px-10 h-auto rounded-full w-full max-w-md md:w-auto whitespace-normal text-center transition-transform duration-200 shadow-xl"
          >
            👉 See My Airbnb Income — Takes 1 Minute
          </Button>
        </div>

        {/* 7. LTR vs. Airbnb Comparison */}
        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Comparison />
          </Suspense>
        </LazySection>

        {/* 8. Dynamic Pricing Demo (PricingEducation) */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <PricingEducation />
          </Suspense>
        </LazySection>

        {/* 10. Pricing Tiers */}
        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Pricing />
          </Suspense>
        </LazySection>

        {/* 11. JNN Homes vs. Typical Co-Host */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CoHostComparison />
          </Suspense>
        </LazySection>

        {/* 12. County-by-County Compliance */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CountyCompliance />
          </Suspense>
        </LazySection>

        {/* 13. Full-Service Management A-Z — alternating photo/text rows */}
        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FullServiceManagement />
          </Suspense>
        </LazySection>

        {/* Watch & learn — featured videos */}
        <LazySection
          minHeight="500px"
          fallback={
            <div className="h-[500px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[500px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <WatchLearnSection />
          </Suspense>
        </LazySection>

        {/* Second Wave Testimonials */}
        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Testimonials variant={2} />
          </Suspense>
        </LazySection>

        {/* 14. FAQ */}
        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FAQ />
          </Suspense>
        </LazySection>

        {/* Recent Articles / Resources */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div className="space-y-3">
                <span className="reveal text-xs font-bold uppercase tracking-[0.25em] text-primary">
                  Resources & Insights
                </span>
                <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl font-heading font-bold">
                  Latest From Our Blog
                </h2>
              </div>
              <Link
                to="/blog"
                className="reveal reveal-delay-2 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:gap-2.5 transition-all"
              >
                View All Articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {recentPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col rounded-2xl overflow-hidden border border-border/60 shadow-sm hover:shadow-xl transition-all bg-card"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <span className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                      {post.category}
                    </span>
                    <h3 className="text-base font-heading font-bold leading-snug mb-3">
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto pt-4 border-t border-border/60">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />{" "}
                        {new Date(post.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {post.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 15. Final CTA Section — property image background */}
        <LazySection
          minHeight="400px"
          fallback={
            <div className="h-[400px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[400px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CTASection />
          </Suspense>
        </LazySection>
      </main>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={
          <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
        }
      >
        <Suspense
          fallback={
            <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
          }
        >
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default Index;
